import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '../utils'
import { AlertTriangleIcon, X } from 'lucide-react'
import { Button } from '../button'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-background-primary shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col items-center text-center sm:text-left', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-text-placeholder', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

type BaseDialogProps = {
  open: boolean
  handleClose: () => void
  className?: string
  children?: React.ReactNode
}

function BaseDialog({ open, handleClose, className, children }: BaseDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className={cn('min-w-[720px] min-h-[456px] flex flex-col', className)}
        onInteractOutside={handleClose}
        onEscapeKeyDown={handleClose}
      >
        {children}
      </DialogContent>
    </Dialog>
  )
}

function BaseDialogTitle({
  title,
  handleClose,
  className,
}: {
  title: string
  handleClose: () => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-6 border-b border-border-secondary max-h-[76px]',
        className,
      )}
    >
      <p className="text-fg-primary-900 text-xl font-semibold">{title}</p>
      <X className="w-5 h-5 cursor-pointer" onClick={handleClose} />
    </div>
  )
}

function BaseDialogContent({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col flex-1 bg-background-secondary px-4 pt-5 pb-6', className)}>
      {children}
    </div>
  )
}

function BaseDialogFooter({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-6 border-t border-border-secondary max-h-[84px]',
        className,
      )}
    >
      {children}
    </div>
  )
}

function ConfirmDialog({
  handleClose,
  isOpen,
  title,
  message,
  action,
}: {
  title: string
  message: string
  isOpen: boolean
  handleClose: () => void
  action: {
    label: string
    onClick: () => void
    loading?: boolean
  }
}) {
  return (
    <BaseDialog open={isOpen} handleClose={handleClose} className="min-w-[442px] min-h-[228px]">
      <BaseDialogTitle title={title} handleClose={handleClose} className="border-none" />
      <BaseDialogContent className=" bg-background-primary pt-0 px-6">
        <p className="text-text-primary-900">{message}</p>
        <div className="flex items-center gap-x-2 mt-2">
          <AlertTriangleIcon className="stroke-fg-error-primary w-4 h-4" />
          <p className="text-text-error-primary-600 font-semibold">This action cannot be undone.</p>
        </div>
      </BaseDialogContent>
      <BaseDialogFooter className="border-none">
        <Button variant={'secondary'} className="w-[191px] h-[44px]" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          className="w-[191px] h-[44px]"
          onClick={action.onClick}
          loading={action.loading}
        >
          {action.label}
        </Button>
      </BaseDialogFooter>
    </BaseDialog>
  )
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  BaseDialog,
  BaseDialogTitle,
  BaseDialogContent,
  BaseDialogFooter,
  ConfirmDialog,
}
